import { engineComponent, ArrayUtils } from "neo-common";
import { BaseController } from "./BaseController";
import { Paylines } from "../Paylines";
import { PaylinesSelector } from "../PaylinesSelector";
import { Symbols } from "../Symbols";
import { Machine, MachineAttributes } from "../Machine";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { GameManager } from "../../Managers/GameManager";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../../Utils/FrameTimer";
import { PlayList } from "../../Components/Sounds/PlayList";
import { DataManager } from "../../Managers/DataManager";
import { StageController } from "../../Components/StageController";
import { ModuleManager } from "../../Managers/ModuleManager";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { WinAnimator } from "../WinAnimator";
import { CustomEase } from "../../Components/Effects/CustomEase";
import { TeasingExtended } from "../../CustomComponents/TeasingExtended";
import { WinAnimatorExtended } from "../../CustomComponents/WinAnimatorExtended";
import { MachineExtended } from "../../CustomComponents/MachineExtended";
import { Tweens } from "../../Components/Effects/Tweens";
import { BetManager } from "../../Managers/BetManager";
import { AmbientBackground } from "../../Utils/AmbientBackground";
import { Configuration } from "../../Components/Configuration";

@engineComponent(ComponentTypes.DisplayObject)
export class BaseView extends ContainerElement {
    public static WinFrame0: SwitchElement;
    public static WinFrame1: SwitchElement;
    public static WinFrame2: SwitchElement;
    public static WinFrame3: SwitchElement;
    public static WinFrame4: SwitchElement;
    public static TeasingBackground: SwitchElement;
    public static TeasingContainer: SwitchElement;
    public static FsMessageBox: SwitchElement;

    public static ShakeAllConf: Array<number>;
    public static ShakeReelConf: Array<number>;

    private _controller: BaseController;
    private _wasFinished: boolean = false;
    private _introDelay: FrameTimerEventEmitter;
    private _outroDelay: FrameTimerEventEmitter;

    private _paylines: Paylines;
    private _symbols: Symbols;
    private _machine: MachineExtended;
    private _winAnimator: WinAnimatorExtended;

    private _teasingExtended: TeasingExtended;

    private _alphaArray: Array<any> = [];

    private _shakeAllArray: Array<any> = [];
    private _shakeAllArrayX: Array<number> = [];
    private _shakeAllArrayY: Array<number> = [];

    private _shakeReel1Array: Array<any> = [];
    private _shakeReel1ArrayX: Array<number> = [];
    private _shakeReel1ArrayY: Array<number> = [];

    private _shakeReel2Array: Array<any> = [];
    private _shakeReel2ArrayX: Array<number> = [];
    private _shakeReel2ArrayY: Array<number> = [];

    private _shakeReel3Array: Array<any> = [];
    private _shakeReel3ArrayX: Array<number> = [];
    private _shakeReel3ArrayY: Array<number> = [];

    private _logoX: number;
    private _logoY: number;

    constructor() {
        super();

        this._introDelay = CreateFrameTimer("introDelay");
        this._introDelay.on(FrameTimerEvents.Complete, this.introFinished.bind(this));

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));

        StageController.baseStageContainer = this;

        CustomEase._createOwnEasing();

        this._teasingExtended = new TeasingExtended();
    }

    createByNode(node: Element): void {
        super.createByNode(node);

        if (this.childs != null) {
            for (let key in this.childs) {
                if (this.childs[key] instanceof Paylines) {
                    this._paylines = this.childs[key];
                }

                if (this.childs[key] instanceof Symbols) {
                    this._symbols = this.childs[key];
                    this.removeChild(this._symbols);
                }

                if (this.childs[key] instanceof MachineExtended) {
                    this._machine = this.childs[key];
                }

                if (this.childs[key] instanceof WinAnimator) {
                    this._winAnimator = this.childs[key];
                }
            }

            if (DEBUG) {
                if (this._paylines == null) {
                    this.log("WARNING no paylines");
                }

                if (this._symbols == null) {
                    this.log("ERROR no symbols");
                }

                if (this._machine == null) {
                    this.log("ERROR no machine");
                }

                if (this._winAnimator == null) {
                    this.log("WARNING no winAnimator");
                }
            }

            if (this.childs["WinFrame0"] != null) BaseView.WinFrame0 = this.childs["WinFrame0"];
            if (this.childs["WinFrame1"] != null) BaseView.WinFrame1 = this.childs["WinFrame1"];
            if (this.childs["WinFrame2"] != null) BaseView.WinFrame2 = this.childs["WinFrame2"];
            if (this.childs["WinFrame3"] != null) BaseView.WinFrame3 = this.childs["WinFrame3"];
            if (this.childs["WinFrame4"] != null) BaseView.WinFrame4 = this.childs["WinFrame4"];
            if (this.childs["TeasingBackground"] != null) BaseView.TeasingBackground = this.childs["TeasingBackground"];
            if (this.childs["TeasingContainer"] != null) BaseView.TeasingContainer = this.childs["TeasingContainer"];
            if (this.childs["FsMessageBox"] != null) BaseView.FsMessageBox = this.childs["FsMessageBox"];
        }

        this._alphaArray.push(this.childs["PaylinesBackground"]);
        this._alphaArray.push(this.childs["SelectedPaylines"]);
        this._alphaArray.push(this.childs["NoWinContainer"]);
        this._alphaArray.push(this.childs["WinContainer"]);

        for (let i: number = 0; i < 5; i++) {
            if (this.childs["ReelBack" + i] != null) {
                this._alphaArray.push(this.childs["ReelBack" + i]);
            }
            if (this.childs["ReelBackMachine" + i] != null) {
                this._alphaArray.push(this.childs["ReelBackMachine" + i]);
            }
            if (this.childs["Reel" + i] != null) {
                this._alphaArray.push(this.childs["Reel" + i]);
            }
        }

        for (let i: number = 0; i < this._alphaArray.length; i++) {
            this._alphaArray[i].visible = false;
            this._alphaArray[i].alpha = 0;
        }

        let config: Element = Configuration.Configurations["ShakeConfiguration"];
        if (config != null) {
            if (config.hasAttribute("ShakeAll")) {
                BaseView.ShakeAllConf = [];
                BaseView.ShakeAllConf = ArrayUtils.numberArray(config.getAttribute("ShakeAll"));
            }
            if (config.hasAttribute("ShakeReel")) {
                BaseView.ShakeReelConf = [];
                BaseView.ShakeReelConf = ArrayUtils.numberArray(config.getAttribute("ShakeReel"));
            }
        }
    }

    disableLogo(): void {
        if (this.childs["Logo"] != null) {
            this.removeChild(this.childs["Logo"]);
        }
    }

    initReceived(controller: BaseController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.baseResponse != null) {
                    if (GameManager.baseResponse.initData != null && GameManager.baseResponse.initData.reels != null) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStopped.bind(this));
                        this._machine.initialize(
                            this._symbols,
                            GameManager.baseResponse.initData.reels,
                            this._teasingExtended,
                            this.childs["Teasing"],
                        );
                    }

                    if (
                        GameManager.baseResponse.screen != null &&
                        GameManager.baseResponse.screen.reels != null &&
                        GameManager.baseResponse.screen.reels.length !== 0
                    ) {
                        if (this._winAnimator != null) {
                            this._winAnimator.on("scatterIndex1", this.scatterIndex1.bind(this));
                            this._winAnimator.on("scatterIndex2", this.scatterIndex2.bind(this));
                            this._winAnimator.on("scatterIndex3", this.scatterIndex3.bind(this));

                            this._winAnimator.on(WinAnimator.ChangeLevel, this.shakeAll.bind(this));
                            this._winAnimator.on(
                                WinAnimator.FinishWinCalculation,
                                this.finishWinCalculation.bind(this),
                            );
                            this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                            this._winAnimator.on(
                                WinAnimator.FinishQuickWinCalculation,
                                this.finishQuickWinCalculation.bind(this),
                            );
                            this._winAnimator.initialize(
                                this._symbols,
                                this._paylines,
                                this.childs["NoWinContainer"],
                                this.childs["WinContainer"],
                            );
                            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
                        }

                        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
                        this._paylines.showPaylinesLabel();
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reels in response");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no base reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }

        this.childs["PaylinesBackground"].goToCase("NORMAL" + (this.hasLineBet1() ? "_LINEBET1" : "_LINEBET5"));
    }

    initIntro(): void {
        StageController.baseStage = true;
        StageController.freespinStage = false;
        for (let i: number = 0; i < this._alphaArray.length; i++) {
            this._alphaArray[i].visible = true;
            this._alphaArray[i].alpha = 1;
        }

        this._winAnimator.showInitWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin);
        if (GameManager.gameResponse.totalWin > 0 || ModuleManager.freespinsEnabled()) {
            PlayList.loop("SND_BaseRecapLoop");
        } else {
            PlayList.loop("SND_BaseLoop");
            PlayList.restoreVolume("SND_BaseLoop");
        }
        if (BaseView.TeasingBackground.getCurrentCase() === "EMPTY" && ModuleManager.freespinsEnabled()) {
            BaseView.TeasingBackground.goToCase("START");
        }

        this._paylines.showPaylinesLabel();
        this.hideMachineReels();
    }

    collect(): void {
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.loop("SND_BaseLoop");
        PlayList.restoreVolume("SND_BaseLoop");
        this._winAnimator.reset(true);
        this.resetShakeAll();
        this.resetShakeReel1();
        this.resetShakeReel2();
        this.resetShakeReel3();
    }

    spin(): void {
        StageController.baseStage = true;
        StageController.freespinStage = false;

        PlayList.stop("SND_BaseRecapLoop");
        PlayList.loop("SND_BaseLoop");
        PlayList.restoreVolume("SND_BaseLoop");

        this._machine.spinStart(this._winAnimator.reset(true, true), true, this._winAnimator.currentSymbols);
        this.showMachineReels();
        this.resetShakeAll();
        this.resetShakeReel1();
        this.resetShakeReel2();
        this.resetShakeReel3();
    }

    stop(): void {
        this._machine.spinStop(null, true);
    }

    spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    onReelStopped(): void {
        this._winAnimator.onReelStopped(GameManager.baseResponse.screen.reels, this._machine.actualReelStopped);
        this.hideMachineReel(this._machine.actualReelStopped);
    }

    machineStopped(): void {
        this._wasFinished = false;
        this._controller.machineStopped();

        if (this._winAnimator.showWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin)) {
            PlayList.setVolume("SND_BaseLoop", 0);
        }
        this.hideMachineReels();
    }

    private finishQuickWinCalculation(): void {
        this._controller.quickWinCalculationFinished();
    }

    finishWinCalculation(): void {
        this._wasFinished = true;
        this._controller.winCalculationFinished();
    }

    finishWinAnimator(): void {
        if (!DataManager.Autoplay || ModuleManager.freespinsEnabled()) {
            PlayList.setVolume("SND_BaseLoop", 0);
            PlayList.loop("SND_BaseRecapLoop");

            if (ModuleManager.freespinsEnabled()) {
                this.shakeAll(10);
                PlayList.stop("SND_BaseRecapLoop");
            }
        }

        this._controller.allWinsFinished();
    }

    resetWinAnimator(): void {
        this._winAnimator.reset(true);
    }

    intro(long: boolean = false): void {
        StageController.baseStage = true;
        this.resetShakeAll();
        if (long) {
            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
            this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
            this._winAnimator.reset(true);

            if (this.childs["Background"] != null) {
                this.childs["Background"].visible = true;
                this.childs["Background"].goToCase("NORMAL");
            }
            for (let i: number = 0; i < this._alphaArray.length; i++) {
                this._alphaArray[i].visible = true;
                Tweens.removeTweens(this._alphaArray[i]);
                Tweens.alphaTween(this._alphaArray[i], {
                    alphas: [0, 1],
                    tweenDuration: 60,
                });
            }
            if (this.childs["Logo"] != null) {
                this.childs["Logo"].visible = true;
                Tweens.removeTweens(this.childs["Logo"]);
                Tweens.alphaTween(this.childs["Logo"], {
                    alphas: [0, 1],
                    tweenDuration: 60,
                });
            }
            this._introDelay.start(60);
        } else {
            for (let i: number = 0; i < this._alphaArray.length; i++) {
                this._alphaArray[i].visible = true;
                Tweens.removeTweens(this._alphaArray[i]);
                Tweens.alphaTween(this._alphaArray[i], {
                    alphas: [0, 1],
                    tweenDuration: 30,
                });
            }
            this.introFinished();
        }
    }

    introFinished(): void {
        StageController.baseStage = true;
        PlayList.stop("SND_FSEndLoop");
        PlayList.stop("SND_FSWin", 30);
        PlayList.stop("SND_FSWait", 30);

        if (GameManager.gameResponse.totalWin > 0) {
            PlayList.setVolume("SND_BaseLoop", 0);
            PlayList.loop("SND_BaseRecapLoop");
        } else {
            PlayList.stop("SND_BaseRecapLoop");
            PlayList.loop("SND_BaseLoop");
            PlayList.restoreVolume("SND_BaseLoop");
        }

        for (let i: number = 0; i < this._alphaArray.length; i++) {
            this._alphaArray[i].visible = true;
            this._alphaArray[i].alpha = 1;
            Tweens.removeTweens(this._alphaArray[i]);
        }

        if (this.childs["Logo"] != null) {
            this.childs["Logo"].visible = true;
            this.childs["Logo"].alpha = 1;
        }
        Tweens.removeTweens(this.childs["Logo"]);

        this._controller.introFinished();
    }

    outro(): void {
        StageController.baseStage = true;

        PlayList.setVolume("SND_BaseLoop", 0);
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.stop("SND_FSWinLoop");

        if (ModuleManager.freespinsActive()) {
            PlayList.play("SND_FSTransition");
            if (BaseView.TeasingBackground != null && BaseView.TeasingBackground.getCurrentCase() === "START") {
                BaseView.TeasingBackground.goToCase("END");
            }
            this._winAnimator.reset(true);
            if (this.childs["Background"] != null) {
                this.childs["Background"].goToCase("TO_FS");
            }

            AmbientBackground.changeAmbientBackground("fs", 1500, 500);

            for (let i: number = 0; i < this._alphaArray.length; i++) {
                this._alphaArray[i].visible = true;
                Tweens.removeTweens(this._alphaArray[i]);
                Tweens.alphaTween(this._alphaArray[i], {
                    alphas: [1, 0],
                    tweenDuration: 30,
                });
            }

            if (this.childs["Logo"] != null) {
                this.childs["Logo"].visible = true;
                Tweens.removeTweens(this.childs["Logo"]);
                Tweens.alphaTween(this.childs["Logo"], {
                    alphas: [1, 0],
                    tweenDuration: 30,
                });
            }

            this._outroDelay.start(210);
        } else {
            const inHighWins = this._winAnimator.reset(true);

            let delay: number = 0;
            if (inHighWins) {
                delay = 30;
            }
            for (let i: number = 0; i < this._alphaArray.length; i++) {
                this._alphaArray[i].visible = true;
                Tweens.removeTweens(this._alphaArray[i]);
                Tweens.alphaTween(this._alphaArray[i], {
                    alphas: [1, 0],
                    tweenDuration: 30,
                    tweenDelay: delay,
                });
            }

            if (this._wasFinished) {
                this._outroDelay.start(30);
            } else {
                if (inHighWins) {
                    this._outroDelay.start(60);
                } else {
                    this._outroDelay.start(30);
                }
            }
        }
    }

    outroFinished(): void {
        for (let i: number = 0; i < this._alphaArray.length; i++) {
            this._alphaArray[i].visible = false;
            Tweens.removeTweens(this._alphaArray[i]);
        }
        if (ModuleManager.freespinsActive()) {
            if (this.childs["Background"] != null) {
                this.childs["Background"].visible = false;
            }
            if (this.childs["Logo"] != null) {
                this.childs["Logo"].visible = false;
            }
        }
        StageController.baseStage = false;
        PlayList.stop("SND_FSWin", 30);
        PlayList.stop("SND_FSWait", 30);
        this._controller.outroFinished();
    }

    selectPayline(id?: number): void {}

    updatePaylines(): void {
        this._winAnimator.softReset();
        this._paylines.setPaylines();

        if (this.childs["SelectedPaylines"] != null) {
            this.childs["SelectedPaylines"].goToCase(
                "SELECT_" + BetManager.PaylineCount + (this.hasLineBet1() ? "_LINEBET1" : "_LINEBET5"),
            );
        }
    }

    showMachineReels(): void {
        for (let i: number = 0; i < 5; i++) {
            if (this.childs["ReelBackMachine" + i] != null) {
                this.childs["ReelBackMachine" + i].visible = true;
            }
        }
    }

    hideMachineReel(index: number): void {
        if (this.childs["ReelBackMachine" + index] != null) {
            this.childs["ReelBackMachine" + index].visible = false;
        }
    }

    hideMachineReels(): void {
        for (let i: number = 0; i < 5; i++) {
            if (this.childs["ReelBackMachine" + i] != null) {
                this.childs["ReelBackMachine" + i].visible = false;
            }
        }
    }

    disableBase(): void {
        this._winAnimator.resetTimer();
    }

    public scatterIndex1(): void {
        if (BaseView.ShakeReelConf == null) {
            return;
        }
        if (this._shakeReel1Array.length === 0) {
            if (this.childs["ReelBack1"] != null) {
                this._shakeReel1ArrayX.push(this.childs["ReelBack1"].x);
                this._shakeReel1ArrayY.push(this.childs["ReelBack1"].y);
                this._shakeReel1Array.push(this.childs["ReelBack1"]);
            }
            if (this.childs["Reel1"] != null) {
                this._shakeReel1ArrayX.push(this.childs["Reel1"].x);
                this._shakeReel1ArrayY.push(this.childs["Reel1"].y);
                this._shakeReel1Array.push(this.childs["Reel1"]);
            }
        }

        this.resetShakeReel1();
        PlayList.play("SND_BaseScatterHit01");

        for (let i: number = 0; i < this._shakeReel1Array.length; i++) {
            Tweens.removeTweens(this._shakeReel1Array[i]);
            Tweens.positionTween(this._shakeReel1Array[i], {
                tweenX: [
                    this._shakeReel1Array[i].x,
                    this._shakeReel1Array[i].x,
                    this._shakeReel1Array[i].x,
                    this._shakeReel1Array[i].x,
                ],
                tweenY: [
                    this._shakeReel1Array[i].y,
                    this._shakeReel1Array[i].y + BaseView.ShakeReelConf[0],
                    this._shakeReel1Array[i].y + BaseView.ShakeReelConf[1],
                    this._shakeReel1Array[i].y,
                ],
                tweenDuration: 10,
            });
        }
    }

    public scatterIndex2(): void {
        if (BaseView.ShakeReelConf == null) {
            return;
        }
        if (this.countScatters(2) < 4) {
            return;
        }

        if (this._shakeReel2Array.length === 0) {
            if (this.childs["ReelBack2"] != null) {
                this._shakeReel2ArrayX.push(this.childs["ReelBack2"].x);
                this._shakeReel2ArrayY.push(this.childs["ReelBack2"].y);
                this._shakeReel2Array.push(this.childs["ReelBack2"]);
            }
            if (this.childs["Reel2"] != null) {
                this._shakeReel2ArrayX.push(this.childs["Reel2"].x);
                this._shakeReel2ArrayY.push(this.childs["Reel2"].y);
                this._shakeReel2Array.push(this.childs["Reel2"]);
            }
        }

        this.resetShakeReel2();

        PlayList.play("SND_BaseScatterHit02");

        for (let i: number = 0; i < this._shakeReel2Array.length; i++) {
            Tweens.removeTweens(this._shakeReel2Array[i]);
            Tweens.positionTween(this._shakeReel2Array[i], {
                tweenX: [
                    this._shakeReel2Array[i].x,
                    this._shakeReel2Array[i].x,
                    this._shakeReel2Array[i].x,
                    this._shakeReel2Array[i].x,
                ],
                tweenY: [
                    this._shakeReel2Array[i].y,
                    this._shakeReel2Array[i].y + BaseView.ShakeReelConf[0],
                    this._shakeReel2Array[i].y + BaseView.ShakeReelConf[1],
                    this._shakeReel2Array[i].y,
                ],
                tweenDuration: 10,
            });
        }
    }

    public scatterIndex3(): void {
        if (BaseView.ShakeReelConf == null) {
            return;
        }
        if (this.countScatters(3) < 7) {
            return;
        }
        if (this._shakeReel3Array.length === 0) {
            if (this.childs["ReelBack3"] != null) {
                this._shakeReel3ArrayX.push(this.childs["ReelBack3"].x);
                this._shakeReel3ArrayY.push(this.childs["ReelBack3"].y);
                this._shakeReel3Array.push(this.childs["ReelBack3"]);
            }
            if (this.childs["Reel3"] != null) {
                this._shakeReel3ArrayX.push(this.childs["Reel3"].x);
                this._shakeReel3ArrayY.push(this.childs["Reel3"].y);
                this._shakeReel3Array.push(this.childs["Reel3"]);
            }
            if (this.childs["Teasing"] != null) {
                this._shakeReel3ArrayX.push(this.childs["Teasing"].x);
                this._shakeReel3ArrayY.push(this.childs["Teasing"].y);
                this._shakeReel3Array.push(this.childs["Teasing"]);
            }
        }

        this.resetShakeReel3();
        PlayList.play("SND_BaseScatterHit03");

        for (let i: number = 0; i < this._shakeReel3Array.length; i++) {
            Tweens.removeTweens(this._shakeReel3Array[i]);
            Tweens.positionTween(this._shakeReel3Array[i], {
                tweenX: [
                    this._shakeReel3Array[i].x,
                    this._shakeReel3Array[i].x,
                    this._shakeReel3Array[i].x,
                    this._shakeReel3Array[i].x,
                ],
                tweenY: [
                    this._shakeReel3Array[i].y,
                    this._shakeReel3Array[i].y + BaseView.ShakeReelConf[0],
                    this._shakeReel3Array[i].y + BaseView.ShakeReelConf[1],
                    this._shakeReel3Array[i].y,
                ],
                tweenDuration: 10,
            });
        }
    }

    private countScatters(reelIndex: number): number {
        let count = 0;

        for (let i = 0; i <= reelIndex; i++) {
            if (GameManager.baseResponse.screen.reels[i].symbols.indexOf(11) !== -1) {
                for (let j = 0; j < GameManager.baseResponse.screen.reels[i].symbols.length; j++) {
                    if (GameManager.baseResponse.screen.reels[i].symbols[j] === 11) {
                        count++;
                    }
                }
            }
        }

        return count;
    }

    public shakeAll(delay: number = 0): void {
        if (BaseView.ShakeAllConf == null) {
            return;
        }

        if (this._shakeAllArray.length === 0) {
            if (this.childs["PaylinesBackground"] != null) {
                this._shakeAllArrayX.push(this.childs["PaylinesBackground"].x);
                this._shakeAllArrayY.push(this.childs["PaylinesBackground"].y);
                this._shakeAllArray.push(this.childs["PaylinesBackground"]);
            }
            if (this.childs["SelectedPaylines"] != null) {
                this._shakeAllArrayX.push(this.childs["SelectedPaylines"].x);
                this._shakeAllArrayY.push(this.childs["SelectedPaylines"].y);
                this._shakeAllArray.push(this.childs["SelectedPaylines"]);
            }
            if (this.childs["NoWinContainer"] != null) {
                this._shakeAllArrayX.push(this.childs["NoWinContainer"].x);
                this._shakeAllArrayY.push(this.childs["NoWinContainer"].y);
                this._shakeAllArray.push(this.childs["NoWinContainer"]);
            }
            if (this.childs["WinContainer"] != null) {
                this._shakeAllArrayX.push(this.childs["WinContainer"].x);
                this._shakeAllArrayY.push(this.childs["WinContainer"].y);
                this._shakeAllArray.push(this.childs["WinContainer"]);
            }
            if (this.childs["WinAnimator"] != null) {
                this._shakeAllArrayX.push(this.childs["WinAnimator"].x);
                this._shakeAllArrayY.push(this.childs["WinAnimator"].y);
                this._shakeAllArray.push(this.childs["WinAnimator"]);
            }
            for (let i: number = 0; i < 5; i++) {
                if (this.childs["ReelBack" + i] != null) {
                    this._shakeAllArrayX.push(this.childs["ReelBack" + i].x);
                    this._shakeAllArrayY.push(this.childs["ReelBack" + i].y);
                    this._shakeAllArray.push(this.childs["ReelBack" + i]);
                }
                if (this.childs["Reel" + i] != null) {
                    this._shakeAllArrayX.push(this.childs["Reel" + i].x);
                    this._shakeAllArrayY.push(this.childs["Reel" + i].y);
                    this._shakeAllArray.push(this.childs["Reel" + i]);
                }
            }
            if (this.childs["Logo"] != null) {
                this._logoX = this.childs["Logo"].x;
                this._logoY = this.childs["Logo"].y;
            }
        }

        this.resetShakeAll();

        for (let i: number = 0; i < this._shakeAllArray.length; i++) {
            Tweens.removeTweens(this._shakeAllArray[i]);
            Tweens.positionTween(this._shakeAllArray[i], {
                tweenX: [
                    this._shakeAllArray[i].x,
                    this._shakeAllArray[i].x,
                    this._shakeAllArray[i].x,
                    this._shakeAllArray[i].x,
                    this._shakeAllArray[i].x,
                    this._shakeAllArray[i].x,
                    this._shakeAllArray[i].x,
                ],
                tweenY: [
                    this._shakeAllArray[i].y,
                    this._shakeAllArray[i].y + BaseView.ShakeAllConf[0],
                    this._shakeAllArray[i].y + BaseView.ShakeAllConf[1],
                    this._shakeAllArray[i].y + BaseView.ShakeAllConf[2],
                    this._shakeAllArray[i].y + BaseView.ShakeAllConf[3],
                    this._shakeAllArray[i].y + BaseView.ShakeAllConf[4],
                    this._shakeAllArray[i].y,
                ],
                tweenDuration: 30,
                tweenDelay: delay,
            });
        }
        if (this.childs["Logo"] != null) {
            Tweens.removeTweens(this.childs["Logo"]);
            Tweens.positionTween(this.childs["Logo"], {
                tweenX: [
                    this.childs["Logo"].x,
                    this.childs["Logo"].x,
                    this.childs["Logo"].x,
                    this.childs["Logo"].x,
                    this.childs["Logo"].x,
                    this.childs["Logo"].x,
                    this.childs["Logo"].x,
                ],
                tweenY: [
                    this.childs["Logo"].y,
                    this.childs["Logo"].y + BaseView.ShakeAllConf[0],
                    this.childs["Logo"].y + BaseView.ShakeAllConf[1],
                    this.childs["Logo"].y + BaseView.ShakeAllConf[2],
                    this.childs["Logo"].y + BaseView.ShakeAllConf[3],
                    this.childs["Logo"].y + BaseView.ShakeAllConf[4],
                    this.childs["Logo"].y,
                ],
                tweenDuration: 30,
                tweenDelay: delay,
            });
        }
    }

    public resetShakeReel1(): void {
        for (let i: number = 0; i < this._shakeReel1Array.length; i++) {
            Tweens.removeTweens(this._shakeReel1Array[i]);
            if (this._shakeReel1ArrayX[i] != null) {
                this._shakeReel1Array[i].x = this._shakeReel1ArrayX[i];
            }
            if (this._shakeReel1ArrayY[i] != null) {
                this._shakeReel1Array[i].y = this._shakeReel1ArrayY[i];
            }
        }
    }

    public resetShakeReel2(): void {
        for (let i: number = 0; i < this._shakeReel2Array.length; i++) {
            Tweens.removeTweens(this._shakeReel2Array[i]);
            if (this._shakeReel2ArrayX[i] != null) {
                this._shakeReel2Array[i].x = this._shakeReel2ArrayX[i];
            }
            if (this._shakeReel2ArrayY[i] != null) {
                this._shakeReel2Array[i].y = this._shakeReel2ArrayY[i];
            }
        }
    }

    public resetShakeReel3(): void {
        for (let i: number = 0; i < this._shakeReel3Array.length; i++) {
            Tweens.removeTweens(this._shakeReel3Array[i]);
            if (this._shakeReel3ArrayX[i] != null) {
                this._shakeReel3Array[i].x = this._shakeReel3ArrayX[i];
            }
            if (this._shakeReel3ArrayY[i] != null) {
                this._shakeReel3Array[i].y = this._shakeReel3ArrayY[i];
            }
        }
    }

    public resetShakeAll(): void {
        this.resetShakeReel1();
        this.resetShakeReel2();
        this.resetShakeReel3();

        for (let i: number = 0; i < this._shakeAllArray.length; i++) {
            Tweens.removeTweens(this._shakeAllArray[i]);
            if (this._shakeAllArrayX[i] != null) {
                this._shakeAllArray[i].x = this._shakeAllArrayX[i];
            }
            if (this._shakeAllArrayY[i] != null) {
                this._shakeAllArray[i].y = this._shakeAllArrayY[i];
            }
        }
        if (this.childs["Logo"] != null) {
            Tweens.removeTweens(this.childs["logo"]);
            if (this._logoX != null) {
                this.childs["Logo"].x = this._logoX;
            }
            if (this._logoY != null) {
                this.childs["Logo"].y = this._logoY;
            }
        }
    }

    /**
     * Toggle all sounds used in base game (background, effects, .etc)
     * @param enable
     */
    toggleSounds(enable: boolean): void {
        if (enable) {
            PlayList.loop("SND_BaseLoop");
        } else {
            PlayList.stop("SND_BaseLoop");
            PlayList.stop("SND_BaseRecapLoop");
        }
    }

    hasLineBet1() {
        return BetManager.PaylineCounts.indexOf(1) !== -1;
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Base/BaseView.ts
 **/